import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import FontAwesome from 'utils/FA';
import { momentFormat, queueTitle } from './helpers';
import customerPath from 'utils/customerPath';

/**
 * represents a single delayed job
 */
const DelayedJobDetails = (
  {
    job: {
      attempts,
      created_at: createdAt,
      run_at: runAt,
      customer,
      handler,
      id,
      last_error: lastError,
      locked_at: lockedAt,
      locked_by: lockedBy,
      priority,
      queue,
      status
    }
  }
) => (
  <div>
    <div className='delayed-job-details'>
      <div>
        <div>
          <span className='delayed-job-details-label'>ID:</span>
          <span className='delayed-job-details-value'>{id}</span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Status:</span>
          <span className='delayed-job-details-value'>
            <FontAwesome name='circle' className={`delayed-job-status-${status}`} />
            &nbsp;
            {queueTitle(status)}
          </span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Queue:</span>
          <span className='delayed-job-details-value'>{queue}</span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Created at:</span>
          <span className='delayed-job-details-value'>
            {moment(createdAt).calendar(null, momentFormat)}
          </span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Run at:</span>
          <span className='delayed-job-details-value'>
            {moment(runAt).calendar(null, momentFormat)}
          </span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Attempts:</span>
          <span className='delayed-job-details-value'>{attempts}</span>
        </div>
        <div>
          <span className='delayed-job-details-label'>Priority:</span>
          <span className='delayed-job-details-value'>{priority}</span>
        </div>
        {
          customer
            ? (
              <div>
                <span className='delayed-job-details-label'>Customer:</span>
                <span className='delayed-job-details-value'>
                  <a href={customerPath(customer)}>{customer}</a>
                </span>
              </div>
            ) : null
        }
        {
          lockedAt
            ? (
              <div>
                <span className='delayed-job-details-label'>Locked at:</span>
                <span className='delayed-job-details-value'>
                  {moment(lockedAt).calendar(null, momentFormat)}
                </span>
              </div>
            ) : null
        }
        {
          lockedBy
            ? (
              <div>
                <span className='delayed-job-details-label'>Host:</span>
                <span className='delayed-job-details-value'>{lockedBy.hostname}</span>
              </div>
            ) : null
        }
        {
          lockedBy
            ? (
              <div>
                <span className='delayed-job-details-label'>PID:</span>
                <span className='delayed-job-details-value'>{lockedBy.pid}</span>
              </div>
            ) : null
        }
      </div>

      <div className='delayed-job-details-label'>Handler:</div>
      <div className='card bg-light'>
        <div className='delayed-job-handler'>
          {
            handler.split('\n').map((line, i) => <div key={i}>{line}</div>)
          }
        </div>
      </div>

      {
        lastError && (
          <div>
            <div className='delayed-job-details-label'>Stack trace:</div>
            <div className='card bg-light'>
              <div className='delayed-job-last-error'>
                {
                  lastError.split('\n').map((line, i) => <div key={i}>{line}</div>)
                }
              </div>
            </div>
          </div>
        )
      }
    </div>
  </div>
);

DelayedJobDetails.propTypes = {
  /** Number of times the job has been attempted */
  attempts: PropTypes.number,

  /** Priority level of the job */
  priority: PropTypes.number,

  /** Creation date */
  created_at: PropTypes.string,

  /** Customer who the job is for, if possible */
  customer: PropTypes.string,

  /** Handler text */
  handler: PropTypes.string,

  /** ID of the job */
  id: PropTypes.number,

  /** The most recent error message */
  last_error: PropTypes.string,

  /** When the job was locked (started running) */
  locked_at: PropTypes.string,

  /** Which host/pid locked the job */
  locked_by: PropTypes.string,

  /** Which queue the job belongs to */
  queue: PropTypes.string,

  /** Job status */
  status: PropTypes.string
};

DelayedJobDetails.defaultProps = {
  attempts: 0,
  priority: 0,
  created_at: '',
  customer: '',
  handler: '',
  id: 0,
  last_error: '',
  locked_at: '',
  locked_by: '',
  queue: '',
  status: ''
};

export default DelayedJobDetails;
